import { Check, AlertCircle, AlertTriangle, ChevronDown } from "lucide-react";
import { cn } from "@/utils";

export type StorySectionState = "done" | "active" | "pending" | "failed" | "warning";

interface StorySectionHeaderProps {
  step: number;
  icon?: React.ReactNode;
  state: StorySectionState;
  title: string;
  meta?: React.ReactNode;
  collapsed: boolean;
  onToggle: () => void;
}

// Each state uses a solid token-backed fill so the rail behind the badges
// never shows through. Pending reads as "next step on the path" via the
// primary-100 tint rather than as disabled (gray-on-gray).
const stateBadgeClass: Record<StorySectionState, string> = {
  done: "bg-[var(--success-bg)] text-[oklch(0.30_0.14_150)]",
  // ringPulse animates box-shadow directly (0→6px spread) so no static shadow needed.
  active: [
    "bg-[var(--primary)] text-white",
    "animate-[ringPulse_1.6s_ease-in-out_infinite]",
  ].join(" "),
  pending: "bg-[var(--primary-100)] text-[var(--primary-700)]",
  failed: "bg-[var(--destructive-bg)] text-[var(--destructive)]",
  warning: "bg-[var(--warning-bg)] text-[var(--warning)]",
};

function StateIcon({
  state,
  step,
  customIcon,
}: {
  state: StorySectionState;
  step: number;
  customIcon?: React.ReactNode;
}) {
  if (customIcon) return <>{customIcon}</>;

  switch (state) {
    case "done":
      return <Check size={12} strokeWidth={2.5} aria-hidden />;
    case "active":
      return <span className="text-[11px] font-bold leading-none">{step}</span>;
    case "failed":
      return <AlertCircle size={12} strokeWidth={2} aria-hidden />;
    case "warning":
      return <AlertTriangle size={12} strokeWidth={2} aria-hidden />;
    case "pending":
    default:
      return <span className="text-[11px] font-bold leading-none">{step}</span>;
  }
}

/**
 * StorySectionHeader — step badge + title + meta + collapse chevron.
 * Extracted to keep story-section.tsx under 200 LoC.
 */
export function StorySectionHeader({
  step,
  icon,
  state,
  title,
  meta,
  collapsed,
  onToggle,
}: StorySectionHeaderProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={!collapsed}
      className={cn(
        "flex items-center gap-2.5 w-full text-left",
        "py-2 px-0 border-0 bg-transparent cursor-pointer",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]",
        "rounded-[var(--radius-sm)]",
        "group"
      )}
    >
      {/* Step badge — `relative z-10` lifts the badge into the same paint
          layer as the absolute-positioned rail in the parent. Without it the
          rail (also absolute) paints on top of the static badge per the CSS
          stacking-order rule for positioned vs static siblings, bleeding the
          gradient line through the centre of the badge fill. */}
      <span
        className={cn(
          "relative z-10",
          "inline-flex items-center justify-center shrink-0",
          "w-[22px] h-[22px] rounded-[var(--radius-full)]",
          "text-[11px] font-bold",
          "transition-[background-color,color,box-shadow] duration-[120ms]",
          stateBadgeClass[state]
        )}
        aria-hidden
      >
        <StateIcon state={state} step={step} customIcon={icon} />
      </span>

      {/* Title + meta */}
      <div className="flex-1 min-w-0">
        <span className="text-[13px] font-semibold text-[var(--foreground)] leading-snug">
          {title}
        </span>
        {meta && (
          <span className="ml-2 text-[11px] text-[var(--muted-foreground)] leading-snug">
            {meta}
          </span>
        )}
      </div>

      {/* Chevron */}
      <ChevronDown
        size={14}
        strokeWidth={1.6}
        aria-hidden
        className={cn(
          "shrink-0 text-[var(--muted-foreground)]",
          "transition-transform duration-[180ms] [transition-timing-function:cubic-bezier(0.2,0,0,1)]",
          collapsed ? "-rotate-90" : "rotate-0"
        )}
      />
    </button>
  );
}
